import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg } from "@/components/royal/shared";

// Royal Signature — Treatments: liquid full-bleed header + filter tabs + card grid.
export default function TreatmentsRoyal() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};
  const treatments = content?.treatments || [];
  const categories = useMemo(() => ["All", ...new Set(treatments.map((t) => t.category).filter(Boolean))], [treatments]);
  const [filter, setFilter] = useState("All");
  const shown = filter === "All" ? treatments : treatments.filter((t) => t.category === filter);
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;

  return (
    <div className="bg-[#FAF8F5]" data-testid="treatments-page">
      <section className="relative flex min-h-[52vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="treatments-hero">
        <RoyalLiquidBg src={s.royal_philosophy_image || ROYAL_IMG.clinic} blur={heroBlur} testid="treatments-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="treatments-eyebrow">The Treatment Menu</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="treatments-title">
                Precision care, tailored to you
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                Doctor-led skin, hair, body and wellness treatments — every protocol designed around your features and your goals.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-16 pt-12 sm:px-8 lg:px-12">
        {categories.length > 1 && (
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="treatments-filters" role="tablist" aria-label="Filter treatments">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  role="tab"
                  aria-selected={filter === c}
                  data-testid={`treatments-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                    filter === c ? "bg-[#0F0F0F] text-white" : "border border-[#E8E2D9] text-[#4A4A4A] hover:border-[#C5A059] hover:text-[#C5A059]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="treatments-grid">
          {shown.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.06}>
              <article className="royal-card group h-full overflow-hidden rounded-2xl" data-testid={`treatment-card-${t.id}`}>
                <div className="overflow-hidden">
                  <img src={t.image} alt={t.name} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C5A059]">{t.category}</p>
                  <h2 className="mt-2 font-serif text-2xl font-normal text-[#0F0F0F]">{t.name}</h2>
                  <p className="mt-2 text-sm font-light leading-relaxed text-[#8A8580]">{t.tagline}</p>
                  <div className="mt-6 flex items-center justify-between border-t border-[#E8E2D9] pt-5">
                    {s.show_prices !== "off" && (
                      <span className="font-serif text-lg font-medium text-[#0F0F0F]">from AED {t.price}</span>
                    )}
                    <button onClick={() => book(t.name)} data-testid={`treatment-book-${t.id}`} className="btn-royal px-5 py-2.5 text-[10px]">
                      Book <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        {shown.length === 0 && (
          <p className="mt-16 text-center text-sm font-light text-[#8A8580]" data-testid="treatments-empty">No treatments in this category yet.</p>
        )}
      </section>
      <RoyalCTA />
    </div>
  );
}
